
import { useState } from "react";
import { TaskComment } from "../../types/tasks";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { MessageSquare } from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface TaskCommentsProps {
  comments: TaskComment[];
  currentUserId?: string;
  currentUserName?: string;
  currentUserAvatar?: string;
  onAddComment: (content: string) => void;
}

const TaskComments = ({
  comments,
  currentUserId,
  currentUserName = "You",
  currentUserAvatar,
  onAddComment,
}: TaskCommentsProps) => {
  const [newComment, setNewComment] = useState("");

  const handleSubmitComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (newComment.trim()) {
      onAddComment(newComment.trim());
      setNewComment("");
    }
  };

  const formatCommentDate = (dateString: string) => {
    const date = new Date(dateString);
    const relativeTime = formatDistanceToNow(date, { addSuffix: true });
    const exactTime = format(date, "PPpp"); // Format: Apr 29, 2023, 1:15 PM
    
    return { relativeTime, exactTime };
  };

  return (
    <div className="space-y-4 mt-4 pt-4 border-t">
      <div className="flex items-center gap-2 mb-4">
        <MessageSquare className="h-5 w-5 text-muted-foreground" />
        <h3 className="font-medium">Comments ({comments.length})</h3>
      </div>

      {comments.length > 0 ? (
        <div className="space-y-4">
          {comments.map((comment) => {
            const { relativeTime, exactTime } = formatCommentDate(comment.createdAt);
            
            return (
              <div key={comment.id} className="flex gap-3">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-muted">
                    {comment.authorName.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-sm">
                        {comment.authorId === currentUserId
                          ? "You"
                          : comment.authorName}
                      </p>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <p className="text-xs text-muted-foreground cursor-help">
                              {relativeTime}
                            </p>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">{exactTime}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                  <p className="mt-1 text-sm">{comment.content}</p>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground italic">No comments yet</p>
      )}

      <form onSubmit={handleSubmitComment} className="space-y-2">
        <Textarea
          placeholder="Add a comment..."
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          className="min-h-[80px]"
        />
        <div className="flex justify-end">
          <Button
            type="submit"
            size="sm"
            disabled={!newComment.trim()}
          >
            Add Comment
          </Button>
        </div>
      </form>
    </div>
  );
};

export default TaskComments;
